import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Lock, Pencil, Trash2 } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const CUST = { new_customer: "New Customer", repeat_customer: "Repeat Customer" };
const LEAD_SOURCES = [
  ["organik", "Organik"], ["whatsapp", "WhatsApp"], ["instagram", "Instagram"], ["tiktok", "TikTok"],
  ["ads", "Ads"], ["walk_in", "Walk-in"], ["referral", "Referral"], ["other", "Lainnya"],
];
const SOURCE_LABEL = Object.fromEntries(LEAD_SOURCES);

export default function Leads() {
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: todayStr(), branch_id: "", count: "", customer_type: "new_customer", source: "whatsapp", notes: "" });

  const load = () => api.get("/leads").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/branches").then((r) => { setBranches(r.data); setForm((f) => ({ ...f, branch_id: r.data[0]?.id || "" })); }).catch(() => {});
    load();
  }, []);

  const openCreate = () => { setEditing(null); setForm({ date: todayStr(), branch_id: branches[0]?.id || "", count: "", customer_type: "new_customer", source: "whatsapp", notes: "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ date: r.date, branch_id: r.branch_id, count: r.count, customer_type: r.customer_type, source: r.source || "whatsapp", notes: r.notes || "" }); setOpen(true); };

  const save = async () => {
    if (!form.branch_id) { toast.error("Cabang wajib"); return; }
    const payload = { ...form, count: Number(form.count) || 0 };
    try {
      if (editing) { await api.patch(`/leads/${editing.id}`, payload); toast.success("Leads diperbarui"); }
      else { await api.post("/leads", payload); toast.success("Leads ditambahkan"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Gagal menyimpan"); }
  };

  const del = async (r) => {
    if (!window.confirm("Hapus entri leads ini?")) return;
    try { await api.delete(`/leads/${r.id}`); toast.success("Leads dihapus"); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Gagal menghapus"); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-emerald-800">Input Leads (CS)</h1>
          <p className="text-sm text-muted-foreground">Catat jumlah leads harian per cabang. Entri terkunci setelah lewat hari.</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-leads-btn" onClick={openCreate}><Plus className="mr-1.5 h-4 w-4" /> Tambah Leads</Button>
      </div>

      <Card className="border-emerald-100">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-emerald-600 hover:bg-emerald-600">
                {["Tanggal", "Cabang", "Jumlah Leads", "Tipe", "Sumber", "Catatan", "Aksi"].map((h) => <TableHead key={h} className="text-white">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Belum ada leads.</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`leads-row-${r.id}`}>
                  <TableCell className="text-sm">{r.date}</TableCell>
                  <TableCell className="text-sm">{branchName(r.branch_id)}</TableCell>
                  <TableCell className="text-sm font-semibold">{r.count}</TableCell>
                  <TableCell className="text-sm">{CUST[r.customer_type] || r.customer_type}</TableCell>
                  <TableCell className="text-sm">{r.source ? (SOURCE_LABEL[r.source] || r.source) : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{r.notes || "—"}</TableCell>
                  <TableCell>
                    {r.locked ? (
                      <Badge variant="outline" className="text-amber-700"><Lock className="mr-1 h-3 w-3" /> Terkunci</Badge>
                    ) : (
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" data-testid={`edit-leads-${r.id}`} onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" className="text-rose-600" data-testid={`del-leads-${r.id}`} onClick={() => del(r)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="leads-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Leads" : "Tambah Leads"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tanggal</Label><Input type="date" data-testid="leads-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <Label>Cabang</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="leads-branch"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Jumlah Leads</Label><Input type="number" min="0" data-testid="leads-count" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} /></div>
            <div>
              <Label>Tipe Customer</Label>
              <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
                <SelectTrigger data-testid="leads-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_customer">New Customer</SelectItem>
                  <SelectItem value="repeat_customer">Repeat Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sumber</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="leads-source"><SelectValue placeholder="Pilih sumber" /></SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Catatan (opsional)</Label><Input data-testid="leads-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="cth. dari IG DM, WA blast, telepon" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="leads-save-btn" onClick={save}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
